import * as Tone from 'tone';
import { state } from './state.js';
import { playSound, synth } from './audio.js';
import {
    SCIFI_PREFIXES,
    SCIFI_MIDDLES,
    SCIFI_SUFFIXES,
    PLANET_COLORS,
    BUNKER_CHANCE_PER_PLANET,
    MAX_BUNKERS_PER_PLANET,
    MIN_BUNKER_RADIUS_FACTOR,
    MAX_BUNKER_RADIUS_FACTOR,
    BUNKER_DEFAULT_WALL_THICKNESS,
    BALL_RADIUS,
    PLAY_AREA_WIDTH,
    PLAY_AREA_HEIGHT,
    STAR_GENERATION_AREA_MULTIPLIER,
    LEVEL_STORAGE_KEY,
    GAME_SPEED_MULTIPLIER
} from './constants.js';

// --- Visual Colors Shades Utility ---
export function shadeColor(color, percent) {
    const num = parseInt(color.replace("#",""),16), amt = Math.round(2.55*percent);
    const R = Math.max(0,Math.min(255,(num>>16)+amt)), G = Math.max(0,Math.min(255,(num>>8&0x00FF)+amt)), B = Math.max(0,Math.min(255,(num&0x0000FF)+amt));
    return "#"+(0x1000000+R*0x10000+G*0x100+B).toString(16).slice(1);
}

// --- Level Name Generator ---
export function generateSciFiName() {
    let name = SCIFI_PREFIXES[Math.floor(Math.random() * SCIFI_PREFIXES.length)];
    if (Math.random() > 0.3) { 
        name += " " + SCIFI_MIDDLES[Math.floor(Math.random() * SCIFI_MIDDLES.length)];
    }
    if (Math.random() > 0.5) { 
         let suffix = SCIFI_SUFFIXES[Math.floor(Math.random() * SCIFI_SUFFIXES.length)];
         if (name.includes(suffix) && SCIFI_SUFFIXES.length > 1) { 
            let attempts = 0;
            while(name.includes(suffix) && attempts < 5) {
                suffix = SCIFI_SUFFIXES[Math.floor(Math.random() * SCIFI_SUFFIXES.length)];
                attempts++;
            }
            if (name.includes(suffix)) suffix = ""; 
         }
         if (suffix) name += " " + suffix;
    }
    return name;
}

// --- Procedural Level Visual Styling ---
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
}

function clampValue(val, min, max) {
    return Math.min(Math.max(val, min), max);
}

export function applyPlanetStyle(planet, isStartPlanet) {
    const glowRGB = hexToRgb(planet.mainColor);
    planet.glowRGB = glowRGB;
    
    // Procedural axis tilt & polar caps visibility
    planet.tiltAngle = Math.random() > 0.45 ? -Math.PI / 4 + (Math.random() - 0.5) * 0.22 : (Math.random() - 0.5) * 0.85;
    planet.hasPolarCaps = Math.random() > 0.32; // 68% chance to have polar caps/hoods
    
    // Choose visual style
    if (isStartPlanet) {
        const rand = Math.random();
        if (rand < 0.4) planet.style = 'cratered';
        else if (rand < 0.8) planet.style = 'gas_giant';
        else planet.style = 'earthlike';
    } else {
        const rand = Math.random();
        if (rand < 0.28) {
            planet.style = 'cratered';
        } else if (rand < 0.56) {
            planet.style = 'gas_giant';
        } else if (rand < 0.82) {
            planet.style = 'ringed';
        } else {
            planet.style = 'earthlike';
        }
    }
    
    planet.craters = [];
    planet.microCraters = [];
    planet.fissures = [];
    planet.maria = [];
    planet.bands = [];
    planet.storms = [];
    planet.rings = [];
    planet.fractures = [];
    planet.shimmerPatches = [];
    planet.continents = [];
    planet.clouds = [];
    planet.rivers = [];
    planet.cities = [];
    
    if (planet.style === 'cratered') {
        // Volcanic Maria / plains
        const numMaria = 2 + Math.floor(Math.random() * 2);
        for (let m = 0; m < numMaria; m++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = Math.random() * (planet.r * 0.45);
            planet.maria.push({
                cx: Math.cos(angle) * dist,
                cy: Math.sin(angle) * dist,
                cr: planet.r * (0.28 + Math.random() * 0.22),
                dist,
                angle,
                opacity: 0.07 + Math.random() * 0.08,
                color: shadeColor(planet.mainColor, -16)
            });
        }

        // Major Craters
        const numCraters = Math.floor(Math.random() * 4) + 6; // 6 to 9 major craters
        for (let c = 0; c < numCraters; c++) {
            let cx, cy, cr, angle, dist, attempts = 0;
            do {
                angle = Math.random() * Math.PI * 2;
                dist = Math.random() * (planet.r * 0.78);
                cx = Math.cos(angle) * dist;
                cy = Math.sin(angle) * dist;
                cr = planet.r * (0.08 + Math.random() * 0.08); // organic crater size
                attempts++;
            } while (attempts < 30 && planet.craters.some(existing => {
                const distBetween = Math.hypot(existing.cx - cx, existing.cy - cy);
                return distBetween < (existing.cr + cr) * 0.65;
            }));
            
            planet.craters.push({
                cx, cy, cr, angle, dist,
                opacity: 0.15 + Math.random() * 0.22
            });
        }

        // Micro-craters
        const numMicro = 12 + Math.floor(Math.random() * 8);
        for (let m = 0; m < numMicro; m++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = Math.random() * (planet.r * 0.85);
            planet.microCraters.push({
                cx: Math.cos(angle) * dist,
                cy: Math.sin(angle) * dist,
                cr: planet.r * (0.015 + Math.random() * 0.022),
                dist,
                angle,
                opacity: 0.18 + Math.random() * 0.25
            });
        }

        // Tectonic Fissures / chasms
        const numFissures = 2 + Math.floor(Math.random() * 2);
        for (let f = 0; f < numFissures; f++) {
            const startAngle = Math.random() * Math.PI * 2;
            const startDist = Math.random() * (planet.r * 0.55);
            let curX = Math.cos(startAngle) * startDist;
            let curY = Math.sin(startAngle) * startDist;
            
            const points = [{ x: curX, y: curY }];
            const fissureLength = planet.r * (0.35 + Math.random() * 0.4);
            const steps = 5 + Math.floor(Math.random() * 4);
            const stepDist = fissureLength / steps;
            let dirAngle = Math.random() * Math.PI * 2;
            
            for (let s = 0; s < steps; s++) {
                const jitteredAngle = dirAngle + (Math.random() - 0.5) * 1.0;
                curX += Math.cos(jitteredAngle) * stepDist;
                curY += Math.sin(jitteredAngle) * stepDist;
                
                if (Math.hypot(curX, curY) < planet.r * 0.85) {
                    points.push({ x: curX, y: curY });
                } else {
                    break;
                }
            }
            if (points.length >= 2) {
                planet.fissures.push({
                    points,
                    opacity: 0.22 + Math.random() * 0.22,
                    width: 0.8 + Math.random() * 0.8
                });
            }
        }
    } else if (planet.style === 'gas_giant') {
        const numBands = Math.floor(Math.random() * 3) + 3; // 3 to 5 bands
        let lastY = -planet.r * 0.85;
        const availableHeight = planet.r * 1.7;
        const spacing = availableHeight / numBands;
        
        for (let b = 0; b < numBands; b++) {
            const bandY = lastY + Math.random() * (spacing * 0.4) + spacing * 0.2;
            const bandH = planet.r * (0.08 + Math.random() * 0.14);
            
            const percent = (Math.random() > 0.5 ? 1 : -1) * (15 + Math.floor(Math.random() * 20));
            const bandColor = shadeColor(planet.mainColor, percent);
            
            planet.bands.push({
                y: clampValue(bandY, -planet.r * 0.85, planet.r * 0.85 - bandH),
                h: bandH,
                color: bandColor
            });
            
            lastY = bandY + bandH;
        }

        // Procedural storms
        const numStorms = Math.floor(Math.random() * 2) + 1; // 1 to 2 storms
        for (let s = 0; s < numStorms; s++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = Math.random() * (planet.r * 0.55);
            const crX = planet.r * (0.16 + Math.random() * 0.12);
            const crY = planet.r * (0.09 + Math.random() * 0.08);
            const stormColor = Math.random() > 0.5 ? shadeColor(planet.mainColor, 35) : shadeColor(planet.mainColor, -40);
            const opacity = 0.5 + Math.random() * 0.35;
            
            // Build trailing eddies for storm turbulence
            const eddies = [];
            const numEddies = 2 + Math.floor(Math.random() * 2);
            const direction = Math.random() > 0.5 ? 1 : -1;
            for (let ed = 1; ed <= numEddies; ed++) {
                eddies.push({
                    offsetX: direction * (crX * 1.35 * ed),
                    offsetY: Math.sin(ed * 1.8) * crY * 0.35,
                    crX: crX * Math.pow(0.72, ed),
                    crY: crY * Math.pow(0.72, ed),
                    opacity: opacity * Math.pow(0.6, ed)
                });
            }

            planet.storms.push({
                cx: Math.cos(angle) * dist,
                cy: Math.sin(angle) * dist,
                dist: dist,
                crX, crY,
                angle: (Math.random() - 0.5) * 0.4,
                color: stormColor,
                opacity,
                eddies
            });
        }
    } else if (planet.style === 'ringed') {
        planet.ringAngle = (Math.random() - 0.5) * 0.5; // Tilted angle
        
        // Procedurally generated rings!
        const numRings = 4 + Math.floor(Math.random() * 5); // 4 to 8 ringlets
        let currentScale = 1.52 + Math.random() * 0.14;
        const ringColorRGB = `${glowRGB.r}, ${glowRGB.g}, ${glowRGB.b}`;
        
        for (let r = 0; r < numRings; r++) {
            if (r > 0 && Math.random() < 0.3) {
                currentScale += 0.07 + Math.random() * 0.08; // Cassini divisions
            }
            const width = 0.03 + Math.random() * 0.13;
            const opacity = 0.1 + Math.random() * 0.58;
            
            planet.rings.push({
                rScale: currentScale + width / 2,
                width: width,
                color: `rgba(${ringColorRGB}, ${opacity})`
            });
            currentScale += width + 0.01 + Math.random() * 0.03;
        }

        // Glacial Europa/Enceladus-style fractures
        const numFractures = 3 + Math.floor(Math.random() * 3);
        for (let f = 0; f < numFractures; f++) {
            const startAngle = Math.random() * Math.PI * 2;
            const startDist = Math.random() * (planet.r * 0.65);
            let curX = Math.cos(startAngle) * startDist;
            let curY = Math.sin(startAngle) * startDist;
            
            const points = [{ x: curX, y: curY }];
            const fracLength = planet.r * (0.35 + Math.random() * 0.45);
            const steps = 6 + Math.floor(Math.random() * 3);
            const stepDist = fracLength / steps;
            let dirAngle = Math.random() * Math.PI * 2;
            
            for (let s = 0; s < steps; s++) {
                const jitteredAngle = dirAngle + (Math.random() - 0.5) * 0.7;
                curX += Math.cos(jitteredAngle) * stepDist;
                curY += Math.sin(jitteredAngle) * stepDist;
                
                if (Math.hypot(curX, curY) < planet.r * 0.88) {
                    points.push({ x: curX, y: curY });
                } else {
                    break;
                }
            }
            if (points.length >= 2) {
                planet.fractures.push({
                    points,
                    opacity: 0.32 + Math.random() * 0.32
                });
            }
        }

        // Frost plateaus (reflective shimmer)
        const numPatches = 3 + Math.floor(Math.random() * 3);
        for (let p = 0; p < numPatches; p++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = Math.random() * (planet.r * 0.55);
            planet.shimmerPatches.push({
                cx: Math.cos(angle) * dist,
                cy: Math.sin(angle) * dist,
                cr: planet.r * (0.12 + Math.random() * 0.16),
                dist,
                angle,
                opacity: 0.08 + Math.random() * 0.18
            });
        }

        // Soft pastel bands
        const numCoreBands = 2 + Math.floor(Math.random() * 2);
        let coreY = -planet.r * 0.75;
        const spacing = (planet.r * 1.5) / numCoreBands;
        for (let b = 0; b < numCoreBands; b++) {
            const bandY = coreY + Math.random() * (spacing * 0.35) + spacing * 0.25;
            const bandH = planet.r * (0.06 + Math.random() * 0.09);
            planet.bands.push({
                y: clampValue(bandY, -planet.r * 0.82, planet.r * 0.82 - bandH),
                h: bandH,
                color: `rgba(${Math.min(255, glowRGB.r + 35)}, ${Math.min(255, glowRGB.g + 35)}, ${Math.min(255, glowRGB.b + 35)}, 0.14)`
            });
            coreY = bandY + bandH;
        }
    } else if (planet.style === 'earthlike') {
        // High fidelity procedural earthlike style properties
        planet.mainColor = '#1B4F72'; // Beautiful deep ocean blue
        planet.secondaryColor = '#0E2C41'; // Dark ocean depth
        planet.glowRGB = { r: 52, g: 152, b: 219 }; // Vibrant blue atmospheric glow
        
        const numContinents = Math.floor(Math.random() * 2) + 2; // 2 to 3 main cohesive continents
        for (let c = 0; c < numContinents; c++) {
            const mainAngle = Math.random() * Math.PI * 2;
            const mainDist = Math.random() * (planet.r * 0.65); // Spaced centers leaving wide ocean spans
            
            const numBlobs = Math.floor(Math.random() * 2) + 2; // 2 to 3 overlapping blobs per continent
            const continentColor = Math.random() > 0.4 ? '#2E7D32' : '#4CAF50'; // Deep lush greens
            
            for (let b = 0; b < numBlobs; b++) {
                const offsetAngle = Math.random() * Math.PI * 2;
                const offsetDist = Math.random() * (planet.r * 0.15); // Cluster closely for cohesive shapes
                
                const cx = Math.cos(mainAngle) * mainDist + Math.cos(offsetAngle) * offsetDist;
                const cy = Math.sin(mainAngle) * mainDist + Math.sin(offsetAngle) * offsetDist;
                const dist = Math.hypot(cx, cy);
                const angle = Math.atan2(cy, cx);
                
                planet.continents.push({
                    cx, cy, dist, angle,
                    cr: planet.r * (0.18 + Math.random() * 0.16), // Slightly larger cohesive blobs
                    color: continentColor
                });
            }
        }

        // Winding rivers branching from mountain/continent centers to the coasts
        const numRivers = Math.min(planet.continents.length, 2 + Math.floor(Math.random() * 2));
        for (let rv = 0; rv < numRivers; rv++) {
            const continent = planet.continents[rv];
            const startX = continent.cx + continent.cr * 0.18;
            const startY = continent.cy + continent.cr * 0.18;
            
            let curX = startX;
            let curY = startY;
            const points = [{ x: curX, y: curY }];
            
            let dirAngle = Math.atan2(curY, curX) + (Math.random() - 0.5) * 0.6;
            const riverLength = continent.cr * (0.75 + Math.random() * 0.35);
            const steps = 6 + Math.floor(Math.random() * 4);
            const stepDist = riverLength / steps;
            
            for (let s = 0; s < steps; s++) {
                const jitteredAngle = dirAngle + (Math.random() - 0.5) * 0.7;
                curX += Math.cos(jitteredAngle) * stepDist;
                curY += Math.sin(jitteredAngle) * stepDist;
                
                if (Math.hypot(curX, curY) < planet.r * 0.95) {
                    points.push({ x: curX, y: curY });
                } else {
                    break;
                }
            }
            if (points.length >= 2) {
                planet.rivers.push({ points });
            }
        }

        // Bioluminescent City Lights clustered near continent coastlines
        planet.continents.forEach((continent) => {
            const numCities = 2 + Math.floor(Math.random() * 2);
            for (let ci = 0; ci < numCities; ci++) {
                const angle = Math.random() * Math.PI * 2;
                // Cluster near the coastline shelf
                const dist = continent.cr * (0.65 + Math.random() * 0.28);
                const cx = continent.cx + Math.cos(angle) * dist;
                const cy = continent.cy + Math.sin(angle) * dist;
                
                if (Math.hypot(cx, cy) < planet.r * 0.92) {
                    planet.cities.push({
                        cx, cy,
                        size: 0.6 + Math.random() * 0.8,
                        opacity: 0.5 + Math.random() * 0.4
                    });
                }
            }
        });
        
        planet.clouds = [];
        const numCloudContinents = Math.floor(Math.random() * 2) + 2; // 2 to 3 main cloud systems
        for (let cc = 0; cc < numCloudContinents; cc++) {
            const mainAngle = Math.random() * Math.PI * 2;
            const mainDist = Math.random() * (planet.r * 0.85); // Spread centers edge-to-edge!
            
            const numBlobs = Math.floor(Math.random() * 3) + 3; // 3 to 5 overlapping blobs per cloud system
            
            for (let b = 0; b < numBlobs; b++) {
                const offsetAngle = Math.random() * Math.PI * 2;
                const offsetDist = Math.random() * (planet.r * 0.16); // Cluster closely
                
                const cx = Math.cos(mainAngle) * mainDist + Math.cos(offsetAngle) * offsetDist;
                const cy = Math.sin(mainAngle) * mainDist + Math.sin(offsetAngle) * offsetDist;
                const dist = Math.hypot(cx, cy);
                const angle = Math.atan2(cy, cx);
                
                planet.clouds.push({
                    cx, cy, dist, angle,
                    crX: planet.r * (0.16 + Math.random() * 0.14), // Perfectly proportioned overlapping fluffy blobs
                    crY: planet.r * (0.09 + Math.random() * 0.09),
                    rot: (Math.random() - 0.5) * 0.6
                });
            }
        }
    }
}

// --- Direct Path Block Check Helper ---
function isDirectPathBlocked(startP, goalP, planets) {
    const ax = startP.x, ay = startP.y;
    const bx = goalP.x, by = goalP.y;
    const vx = bx - ax, vy = by - ay;
    const vLenSq = vx * vx + vy * vy;
    if (vLenSq === 0) return false;
    
    for (const p of planets) {
        if (p === startP || p === goalP) continue;
        
        const ux = p.x - ax, uy = p.y - ay;
        let t = (ux * vx + uy * vy) / vLenSq;
        t = Math.max(0, Math.min(1, t)); // clamp to segment
        
        const closestX = ax + t * vx;
        const closestY = ay + t * vy;
        const dist = Math.hypot(p.x - closestX, p.y - closestY);
        
        if (dist < p.r + 35) {
            return true; // direct line of sight blocked!
        }
    }
    return false;
}

// --- Procedural Level Generator ---
export function generateLevel(holeIndex = null) {
    console.log('Generating random level with holeIndex:', holeIndex);
    
    let difficulty = 'medium';
    if (holeIndex !== null) {
        if (holeIndex <= 1) difficulty = 'easy';
        else if (holeIndex <= 5) difficulty = 'medium';
        else difficulty = 'hard';
    } else {
        const rand = Math.random();
        if (rand < 0.25) difficulty = 'easy';
        else if (rand < 0.70) difficulty = 'medium';
        else difficulty = 'hard';
    }

    let maxAttempts = 12;
    let finalLevel = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        let planetCount;
        let bunkerChanceMultiplier;
        
        if (difficulty === 'easy') {
            planetCount = Math.floor(Math.random() * 2) + 2; // 2 to 3 planets
            bunkerChanceMultiplier = 0.3;
        } else if (difficulty === 'medium') {
            planetCount = Math.floor(Math.random() * 2) + 3; // 3 to 4 planets
            bunkerChanceMultiplier = 1.0;
        } else { // hard
            planetCount = Math.floor(Math.random() * 3) + 4; // 4 to 6 planets
            bunkerChanceMultiplier = 1.8;
        }

        const newPlanets = [];
        const margin = Math.min(PLAY_AREA_WIDTH, PLAY_AREA_HEIGHT) * 0.15;
        
        for (let i=0; i<planetCount; i++) {
            let x, y, r, placementAttempts = 0;
            do {
                x = Math.random()*(PLAY_AREA_WIDTH-2*margin)+margin; 
                y = Math.random()*(PLAY_AREA_HEIGHT-2*margin)+margin;
                r = Math.random()*40+40; 
                placementAttempts++;
                if(placementAttempts>100){console.warn("Warn: Planet placement fail."); break;}
            } while(newPlanets.some(p=>Math.hypot(p.x-x,p.y-y)<p.r+r+150) || (i === planetCount - 1 && newPlanets.length > 0 && Math.hypot(newPlanets[0].x-x, newPlanets[0].y-y) < 550 && Math.random() < 0.72)); 
            
            const colorIndex = Math.floor(Math.random()*PLANET_COLORS.length);
            const newPlanet = {
                x, y, r, mass: r*r*0.7, 
                mainColor: PLANET_COLORS[colorIndex], 
                secondaryColor: shadeColor(PLANET_COLORS[colorIndex],-30), 
                atmosphere: Math.random()>0.3,
                bunkers: []
            };
            applyPlanetStyle(newPlanet, i === 0);

            const adjustedBunkerChance = BUNKER_CHANCE_PER_PLANET * bunkerChanceMultiplier;
            if (i > 0 && Math.random() < adjustedBunkerChance) {
                for (let b = 0; b < MAX_BUNKERS_PER_PLANET; b++) {
                    const minRadiusBasedOnPlanet = r * MIN_BUNKER_RADIUS_FACTOR;
                    const maxRadiusBasedOnPlanet = r * MAX_BUNKER_RADIUS_FACTOR;
                    const randomRadiusInRange = minRadiusBasedOnPlanet + Math.random() * (maxRadiusBasedOnPlanet - minRadiusBasedOnPlanet);
                    const bunkerRadius = Math.max(BALL_RADIUS * 3.0, randomRadiusInRange); 

                    let bunkerAngle;
                    let isOverlappingGoal;
                    let bunkerPlacementAttempts = 0;
                    do {
                        isOverlappingGoal = false;
                        bunkerAngle = Math.random() * 2 * Math.PI;
                        if (i === planetCount - 1) { 
                            const goalAngle = Math.random() * 2 * Math.PI; 
                            const goalHoleRadius = 14;
                            const bunkerSurfaceX = x + r * Math.cos(bunkerAngle);
                            const bunkerSurfaceY = y + r * Math.sin(bunkerAngle);
                            const goalHoleX = x + r * Math.cos(goalAngle);
                            const goalHoleY = y + r * Math.sin(goalAngle);
                            if (Math.hypot(bunkerSurfaceX - goalHoleX, bunkerSurfaceY - goalHoleY) < bunkerRadius + goalHoleRadius + 5) {
                               isOverlappingGoal = true;
                            }
                        }
                        bunkerPlacementAttempts++;
                        if (bunkerPlacementAttempts > 20) {
                            console.warn(`Warn: Could not place bunker on planet ${i} without overlapping potential goal area. Skipping bunker.`);
                            isOverlappingGoal = false; 
                            break;
                        }
                    } while (isOverlappingGoal);

                    if (bunkerPlacementAttempts <= 20) {
                         newPlanet.bunkers.push({
                            angle: bunkerAngle,
                            radius: bunkerRadius,
                            wallThickness: BUNKER_DEFAULT_WALL_THICKNESS 
                        });
                    }
                }
            }
            newPlanets.push(newPlanet);
        }
        
        const startPlanet = newPlanets[0];
        const goalPlanetIndex = newPlanets.length - 1;
        const goalPlanet = newPlanets[goalPlanetIndex];
        let goalAngleOnPlanet = Math.random() * 2 * Math.PI; 
        let goalPlacementAttempts = 0;
        let goalAngleValid = false;
        
        do {
            goalAngleOnPlanet = Math.random() * 2 * Math.PI;
            goalAngleValid = true;
            
            const goalHoleX = goalPlanet.r * Math.cos(goalAngleOnPlanet);
            const goalHoleY = goalPlanet.r * Math.sin(goalAngleOnPlanet);
            
            // 1. Must not overlap any bunkers on the planet
            if (goalPlanet.bunkers && goalPlanet.bunkers.length > 0) {
                for (const bunker of goalPlanet.bunkers) {
                    const bunkerSurfaceX = goalPlanet.r * Math.cos(bunker.angle);
                    const bunkerSurfaceY = goalPlanet.r * Math.sin(bunker.angle);
                    if (Math.hypot(goalHoleX - bunkerSurfaceX, goalHoleY - bunkerSurfaceY) < 14 + bunker.radius + 5) { 
                        goalAngleValid = false;
                        break;
                    }
                }
            }
            
            // 2. If it is an Earth-like planet, the flag MUST be planted on a continent landmass
            if (goalAngleValid && goalPlanet.style === 'earthlike' && goalPlanet.continents && goalPlanet.continents.length > 0) {
                let landFound = false;
                for (const continent of goalPlanet.continents) {
                    const u = continent.dist / goalPlanet.r;
                    const squashFactor = Math.sqrt(Math.max(0.12, 1 - u * u));
                    
                    const dx = goalHoleX - continent.cx;
                    const dy = goalHoleY - continent.cy;
                    
                    const cosA = Math.cos(-continent.angle);
                    const sinA = Math.sin(-continent.angle);
                    const rx = dx * cosA - dy * sinA;
                    const ry = dx * sinA + dy * cosA;
                    
                    const termX = rx / (continent.cr * squashFactor);
                    const termY = ry / continent.cr;
                    
                    if (termX * termX + termY * termY <= 1.05) { 
                        landFound = true;
                        break;
                    }
                }
                if (!landFound) {
                    goalAngleValid = false;
                }
            }
            
            goalPlacementAttempts++;
            if (goalPlacementAttempts > 150) {
                console.warn("Warn: Could not place goal perfectly. Breaking placement loop & clearing bunkers on goal planet.");
                goalPlanet.bunkers = [];
                goalAngleValid = true;
                break;
            }
        } while (!goalAngleValid);

        // Check if direct path is blocked by intermediate planets
        const isBlocked = isDirectPathBlocked(startPlanet, goalPlanet, newPlanets);

        // easy should not have blocked direct paths (try to get non-blocked)
        if (difficulty === 'easy' && isBlocked && attempt < maxAttempts - 1) {
            continue; 
        }

        // hard should ideally have blocked direct paths (try to get blocked)
        if (difficulty === 'hard' && !isBlocked && attempt < maxAttempts - 1) {
            continue; 
        }

        const distanceBetweenStartAndGoal = Math.hypot(goalPlanet.x-startPlanet.x, goalPlanet.y-startPlanet.y);
        const baseShots = distanceBetweenStartAndGoal / 550;
        const sizeDifficulty = (60 - goalPlanet.r) / 20; 
        const sizeDistancePenalty = sizeDifficulty * (distanceBetweenStartAndGoal / 1000);
        const obstacleFactor = Math.max(0, planetCount - 3) * 0.4;
        const blockedPenalty = isBlocked ? 1.0 : 0.0;
        
        const rawPar = 1.4 + baseShots + sizeDistancePenalty + obstacleFactor + blockedPenalty;
        let calculatedPar = Math.round(rawPar);
        
        if (isBlocked) {
            calculatedPar = Math.max(3, calculatedPar);
        } else {
            calculatedPar = Math.max(2, calculatedPar);
        }
        const levelName = generateSciFiName();
        
        finalLevel = {
            planets: newPlanets, 
            goal: { planetIndex: goalPlanetIndex, angleOnPlanet: goalAngleOnPlanet, holeRadius: 14, flagHeight: 35 }, 
            par: calculatedPar,
            name: levelName,
            spawnAngle: Math.random() * 2 * Math.PI,
            difficulty: difficulty
        };
        break;
    }

    console.log(`Generated level: "${finalLevel.name}" (${finalLevel.difficulty}). Planets: ${finalLevel.planets.length}. Par: ${finalLevel.par}`);
    return finalLevel;
}

export function generateCourse(count = 9) {
    const course = [];
    for (let i = 0; i < count; i++) {
        course.push(generateLevel(i));
    }
    return course;
}

// --- Fireworks System ---
export function createFireworks(x, y) {
    const count = 5 + Math.floor(Math.random() * 5);
    for (let i = 0; i < count; i++) {
        state.fireworks.push({
            x: x, y: y, 
            vx: (Math.random() - 0.5) * 80, 
            vy: -Math.random() * 50 - 80, 
            color: `hsl(${Math.random() * 360},100%,70%)`, 
            particles: [], 
            exploded: false, 
            life: 1.0
        });
    }
    const now = Tone.now();
    playSound(synth, "C5", "8n", now);
    playSound(synth, "G5", "8n", now + 0.1 * (1 / GAME_SPEED_MULTIPLIER)); 
    playSound(synth, "C6", "8n", now + 0.2 * (1 / GAME_SPEED_MULTIPLIER));
}

// --- Stars Generator ---
export function initStars() {
    state.stars = [];
    const genAreaWidth = PLAY_AREA_WIDTH * STAR_GENERATION_AREA_MULTIPLIER;
    const genAreaHeight = PLAY_AREA_HEIGHT * STAR_GENERATION_AREA_MULTIPLIER;
    const genOffsetX = (PLAY_AREA_WIDTH - genAreaWidth) / 2;
    const genOffsetY = (PLAY_AREA_HEIGHT - genAreaHeight) / 2;

    for (let i = 0; i < 200; i++) {
        state.stars.push({
            initialX: Math.random() * genAreaWidth + genOffsetX,
            initialY: Math.random() * genAreaHeight + genOffsetY,
            s: Math.random() * 1.5 + 0.5
        });
    }
}

// --- Levels Saving Actions ---
export function saveCurrentLevel(levelName) {
    if (!levelName || levelName.trim() === "") {
        alert("Please enter a valid name for the level.");
        return false;
    }
    const currentLevelData = {
        name: levelName.trim(),
        planets: state.gameState.planets.map(p => ({...p})), 
        goal: {...state.gameState.goal}, 
        par: state.currentPar,
        timestamp: new Date().toISOString()
    };
    if (state.savedLevels.some(level => level.name === currentLevelData.name)) {
        if (!confirm(`A level named "${currentLevelData.name}" already exists. Overwrite it?`)) {
            return false;
        }
        state.savedLevels = state.savedLevels.filter(level => level.name !== currentLevelData.name);
    }
    state.savedLevels.push(currentLevelData);
    localStorage.setItem(LEVEL_STORAGE_KEY, JSON.stringify(state.savedLevels));
    console.log(`Level "${currentLevelData.name}" saved.`);
    return true;
}

export function loadSavedLevels() {
    const storedLevels = localStorage.getItem(LEVEL_STORAGE_KEY);
    if (storedLevels) {
        state.savedLevels = JSON.parse(storedLevels);
    } else {
        state.savedLevels = [];
    }
}
